// The layer's shared style props, turned into Bootstrap classes. Every component in src/ui takes
// them, so pages never name a framework class: another framework's src/ui only has to map the
// same props to its own classes. Anything else stays a named page class in index.css.
//
// Spacing: m mt mb ms me mx my p pt pb ps pe px py, on the 0–5 scale (0, .25, .5, 1, 1.5, 3 rem),
//   or 'auto' for margins.
// Text: muted, tone ('danger' | 'primary' | 'good' | 'bad' | 'warning' | 'onDarkMuted'),
//   text ('small' | 'lead' | 'md' | 'lg' | 'xl'), weight ('normal' | 'semibold' | 'bold'),
//   textAlign ('start' | 'center' | 'end'), nowrap, truncate, tight (line height), noUnderline,
//   inheritColor, linkTone ('muted' | 'danger': a link's colour, underline included).
// Layout: display ('block' | 'inline' | 'inline-block' | 'flex' | 'inline-flex' | 'table-cell'),
//   hideBelow ('sm' | 'md': hidden on narrower screens), hideAbove ('sm' | 'md'), grow, fullWidth,
//   autoWidth, position ('relative' | 'absolute'), valign ('baseline' | 'middle').
// Flex (with display flex/inline-flex): gap (0–5), align, justify ('start' | 'center' | 'end' |
//   'between' | 'baseline'), wrap, column.
// Box: bordered, borderTop, borderless, rounded (true or 1), shadow, surface (page background),
//   dimmed, unstyledList.

const SPACING = ['m', 'mt', 'mb', 'ms', 'me', 'mx', 'my', 'p', 'pt', 'pb', 'ps', 'pe', 'px', 'py']
const TONES = {
  danger: 'text-danger',
  primary: 'text-primary',
  good: 'text-success-emphasis',
  bad: 'text-danger-emphasis',
  warning: 'text-warning-emphasis',
  onDarkMuted: 'text-white-50',
}
const TEXT = { small: 'small', lead: 'lead', md: 'fs-6', lg: 'fs-5', xl: 'fs-4' }
const WEIGHT = { normal: 'fw-normal', semibold: 'fw-semibold', bold: 'fw-bold' }
const FLEX = { start: 'start', center: 'center', end: 'end', between: 'between', baseline: 'baseline' }
const LINK_TONE = { muted: 'link-secondary', danger: 'link-danger' }

export const cx = (...names) => names.filter(Boolean).join(' ')

// sx(props, defaults) → [className, props left over for the element]. defaults.display is the
// element's natural display, used by hideBelow (e.g. 'table-cell' for table cells).
export function sx(props, defaults = {}) {
  const {
    className, muted, tone, text, weight, textAlign, nowrap, truncate, tight, noUnderline, inheritColor, linkTone,
    display, hideBelow, hideAbove, grow, fullWidth, autoWidth, position, valign,
    gap, align, justify, wrap, column,
    bordered, borderTop, borderless, rounded, shadow, surface, dimmed, unstyledList,
    ...rest
  } = props
  const out = []
  for (const key of SPACING) {
    if (rest[key] != null) out.push(`${key}-${rest[key]}`)
    delete rest[key]
  }
  const shown = display ?? defaults.display
  if (hideBelow) out.push('d-none', `d-${hideBelow}-${shown ?? 'block'}`)
  else if (display) out.push(`d-${display}`)
  if (hideAbove) out.push(`d-${hideAbove}-none`)
  if (column) out.push('flex-column')
  if (wrap) out.push('flex-wrap')
  if (gap != null) out.push(`gap-${gap}`)
  if (align) out.push(`align-items-${FLEX[align]}`)
  if (justify) out.push(`justify-content-${FLEX[justify]}`)
  if (grow) out.push('flex-grow-1')
  if (muted) out.push('text-body-secondary')
  if (tone) out.push(TONES[tone])
  if (linkTone) out.push(LINK_TONE[linkTone])
  if (inheritColor) out.push('text-reset')
  if (text) out.push(TEXT[text])
  if (weight) out.push(WEIGHT[weight])
  if (textAlign) out.push(`text-${textAlign}`)
  if (nowrap) out.push('text-nowrap')
  if (truncate) out.push('text-truncate')
  if (tight) out.push('lh-sm')
  if (noUnderline) out.push('text-decoration-none')
  if (fullWidth) out.push('w-100')
  if (autoWidth) out.push('w-auto')
  if (position) out.push(`position-${position}`)
  if (valign) out.push(`align-${valign}`)
  if (bordered) out.push('border')
  if (borderTop) out.push('border-top')
  if (borderless) out.push('border-0')
  if (rounded) out.push(rounded === true ? 'rounded' : `rounded-${rounded}`)
  if (shadow) out.push('shadow-sm')
  if (surface) out.push('bg-body')
  if (dimmed) out.push('opacity-75')
  if (unstyledList) out.push('list-unstyled')
  return [cx(...out, className) || undefined, rest]
}
